import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge, Button, Field, RadioGroup, Tabs } from '@highlight/ui'
import { useAuth } from '@renderer/chat/providers'
import {
  useBindLocalSide,
  useDismissSkillComparison,
  useGetRealChatId,
  useLocalCompareSide,
  useOpenLocalPair,
} from '@renderer/chat/store'
import {
  getConversationCompareStamp,
  pickSkillComparisonPair,
  type SkillComparePick,
  type SkillCompareSide,
} from '@renderer/chat/hooks/skill-compare-api'
import { sideLabel } from '@renderer/chat/hooks/skill-compare-plan'
import {
  SKILL_COMPARISON_QUERY_KEY,
  useActiveSkillComparisons,
  useSkillCompareMode,
} from '@renderer/chat/hooks/use-skill-compare'

const LABELS = ['A', 'B'] as const

interface SkillCompareBarProps {
  chatId: string | undefined
  /** Shows the other side's conversation in the view that hosts the bar. */
  onSwitchSide: (conversationId: string) => void
}

/** Dev-only strip over a conversation that is one side of a live comparison: switch sides, then pick. A/B keeps the pick blind. */
export function SkillCompareBar({ chatId, onSwitchSide }: SkillCompareBarProps) {
  const mode = useSkillCompareMode()
  // The side is known locally from the moment the submit starts; the poll only adds how the pair resolved.
  const local = useLocalCompareSide(chatId)
  const comparisons = useActiveSkillComparisons()
  const getRealChatId = useGetRealChatId()
  const { getAuthToken } = useAuth()
  const dismiss = useDismissSkillComparison()
  const queryClient = useQueryClient()

  const pairId = local?.pairId
  const comparisonId = local?.comparisonId
  const pair = comparisons.find((c) => c.id === comparisonId)?.pairs.find((p) => p.id === pairId)

  // A pair the gateway opened (a scheduled fire) is only known from the stamp on its conversations.
  const openPair = useOpenLocalPair()
  const bindSide = useBindLocalSide()
  const { data: stamp } = useQuery({
    queryKey: ['skill-compare-stamp', chatId],
    queryFn: () => (chatId ? getConversationCompareStamp(chatId, getAuthToken) : null),
    enabled: mode && !local && !!chatId,
    retry: false,
    staleTime: Infinity,
  })
  useEffect(() => {
    if (!stamp || !chatId) return
    openPair(stamp)
    bindSide(stamp.pairId, stamp.side, chatId)
  }, [stamp, chatId, openPair, bindSide])

  // Learn each side's real id as soon as the temp→real mapping or the poll reveals it: the view drops
  // the mapping once a chat's messages load, and only the real id opens a chat that was already read.
  useEffect(() => {
    if (!local) return
    for (const s of ['old', 'new'] as const) {
      for (const id of local.chats[s]) {
        const real = getRealChatId(id)
        if (real !== id) bindSide(local.pairId, s, real)
      }
      const fromServer = pair?.[s].conversationId
      if (fromServer) bindSide(local.pairId, s, fromServer)
    }
  }, [local, pair, getRealChatId, bindSide])

  const pick = useMutation({
    mutationFn: (choice: SkillComparePick) => {
      if (!pairId) throw new Error('No comparison pair for this chat.')
      return pickSkillComparisonPair(pairId, choice, getAuthToken)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SKILL_COMPARISON_QUERY_KEY, comparisonId] })
    },
  })

  if (!mode || !local) return null
  const { side, leftIsNew, skillSlug } = local
  const otherSide: SkillCompareSide = side === 'old' ? 'new' : 'old'
  const label = sideLabel(leftIsNew, side)
  const otherLabel = label === 'A' ? 'B' : 'A'
  // Newest known id first: the real one once bound, else the temp id while its mapping still resolves.
  const otherChatId = local.chats[otherSide].at(-1)
  const otherConversationId = pair?.[otherSide].conversationId ?? (otherChatId ? getRealChatId(otherChatId) : null)
  const sideFor = (l: 'A' | 'B') => (l === label ? side : otherSide)

  return (
    <div className="bg-background flex flex-col gap-3 border-b px-3 py-3">
      <div className="flex items-center gap-3">
        <Tabs
          value={label}
          onValueChange={(next) => {
            if (next !== label && otherConversationId) onSwitchSide(otherConversationId)
          }}
        >
          <Tabs.List aria-label="Compare side" className="w-28">
            {LABELS.map((l) => (
              <Tabs.Tab key={l} value={l} disabled={l !== label && !otherConversationId}>
                {l}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        <div className="flex flex-col text-xs">
          <span className="text-foreground flex items-center gap-2 text-sm font-semibold">
            You are reading side {label}
            <Badge tone="brand" size="sm" className="uppercase tracking-wide">
              Alpha only
            </Badge>
          </span>
          <span className="text-foreground-secondary">
            Skill compare · {skillSlug}
            {otherConversationId
              ? ` · click ${otherLabel} to read the other side`
              : ` · side ${otherLabel} is not ready yet`}
          </span>
        </div>
      </div>
      {pair?.resolvedBy ? (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-foreground font-semibold">
            {pair.pick === 'tie'
              ? 'Submitted: tie'
              : `Submitted: preferred ${sideLabel(leftIsNew, pair.pick === 'new' ? 'new' : 'old')}`}
          </span>
          <Button variant="ghost" onClick={() => dismiss(local.comparisonId)}>
            Dismiss
          </Button>
        </div>
      ) : (
        <PickForm
          key={local.pairId}
          choices={[
            ...LABELS.map((l) => ({
              value: sideFor(l),
              label: `Prefer ${l}`,
              disabled: l !== label && !otherConversationId,
            })),
            { value: 'tie' as const, label: 'Tie', disabled: false },
          ]}
          pending={pick.isPending}
          error={pick.isError ? (pick.error as Error).message : null}
          onSubmit={(choice) => pick.mutate(choice)}
        />
      )}
    </div>
  )
}

interface PickChoice {
  value: SkillComparePick
  label: string
  disabled: boolean
}

/** Radio-style choices that only count once submitted, so a stray click cannot resolve the pair. */
function PickForm({
  choices,
  pending,
  error,
  onSubmit,
}: {
  choices: Array<PickChoice>
  pending: boolean
  error: string | null
  onSubmit: (choice: SkillComparePick) => void
}) {
  const [choice, setChoice] = useState<SkillComparePick | null>(null)
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Field>
        <RadioGroup
          value={choice}
          onValueChange={(next) => setChoice(next as SkillComparePick)}
          aria-label="Your pick"
          disabled={pending}
          className="flex-row flex-wrap gap-3"
        >
          {choices.map((c) => (
            <Field.Item key={c.value} disabled={c.disabled}>
              <RadioGroup.Item value={c.value} />
              <Field.Label>{c.label}</Field.Label>
            </Field.Item>
          ))}
        </RadioGroup>
      </Field>
      <Button
        variant="primary"
        className="ml-auto"
        disabled={choice === null || pending}
        loading={pending}
        onClick={() => choice !== null && onSubmit(choice)}
      >
        Submit
      </Button>
      {error && <span className="text-danger-subtle basis-full text-xs">{error}</span>}
    </div>
  )
}
